import { Modal, Col, Row } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  SET_BILLET_REMIT,
  SET_PIX_REMIT,
  SET_TRANSFER_REMIT,
} from '@portal/actions/actionTypes';
import useReduxState from '@portal/hooks/useReduxState';
import { translate } from '@portal/services/i18n';

import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';

import Form from './Form/Form';
import { TypeRemit } from '@portal/models/reducers';

interface RemitModalInterface {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  title: string;
  type: TypeRemit;
}
export interface Approvers {
  name: string;
  value: string;
}

export interface RemitInputs {
  minLimit: number;
  maxLimit: number;
  approvers: Approvers[];
  minApprovers: string;
}

const RemitModal = ({
  setVisible,
  visible,
  title,
  type,
}: RemitModalInterface) => {
  const {
    group: { myGroups, isEdit, showGroup },
  } = useReduxState();

  const dispatch = useDispatch();

  const [rows, setRows] = useState<RemitInputs[]>([
    {
      approvers: [],
      maxLimit: 0,
      minApprovers: '',
      minLimit: 0,
    },
  ]);

  const [modalInvalid, setModalInvalid] = useState(true);

  const mapperShowGroupRemit = useCallback(
    (type: TypeRemit): RemitInputs[] => {
      if (type === 'transfer') {
        return showGroup?.accountTransactionRules.transfer?.map((item) => ({
          approvers: item.group.map((group) => ({
            name: group.name,
            value: group.id,
          })),
          maxLimit: item.max,
          minApprovers: item.min_approver.toString(),
          minLimit: item.min,
        })) as RemitInputs[];
      }

      if (type === 'billet') {
        return showGroup?.accountTransactionRules.billet?.map((item) => ({
          approvers: item.group.map((group) => ({
            name: group.name,
            value: group.id,
          })),
          maxLimit: item.max,
          minApprovers: item.min_approver.toString(),
          minLimit: item.min,
        })) as RemitInputs[];
      }

      if (type === 'pix') {
        return showGroup?.accountTransactionRules.pix?.map((item) => ({
          approvers: item.group.map((group) => ({
            name: group.name,
            value: group.id,
          })),
          maxLimit: item.max,
          minApprovers: item.min_approver.toString(),
          minLimit: item.min,
        })) as RemitInputs[];
      }

      return [
        {
          approvers: [],
          maxLimit: 0,
          minApprovers: '',
          minLimit: 0,
        },
      ];
    },
    [
      showGroup?.accountTransactionRules.billet,
      showGroup?.accountTransactionRules.pix,
      showGroup?.accountTransactionRules.transfer,
    ]
  );

  useEffect(() => {
    if (isEdit && visible) {
      setRows(mapperShowGroupRemit(type));
    }
  }, [isEdit, mapperShowGroupRemit, type, visible]);

  useEffect(() => {
    rows.forEach((item, index) => {
      if (item.approvers.length === 0 && index + 1 > 1) {
        setModalInvalid(true);
        return;
      }

      if (item.approvers.length < Number(item.minApprovers)) {
        setModalInvalid(true);
        return;
      }

      setModalInvalid(false);
    });
  }, [rows]);

  const handleClose = () => {
    setRows([
      {
        approvers: [],
        maxLimit: 0,
        minApprovers: '',
        minLimit: 0,
      },
    ]);
    setVisible(false);
  };

  const save = useCallback(() => {
    switch (type) {
      case 'transfer':
        dispatch({ type: SET_TRANSFER_REMIT, payload: rows });
        break;
      case 'billet':
        dispatch({ type: SET_BILLET_REMIT, payload: rows });
        break;
      case 'pix':
        dispatch({ type: SET_PIX_REMIT, payload: rows });
        break;
    }

    setVisible(false);
  }, [dispatch, rows, setVisible, type]);

  const deleteRow = useCallback(
    (index: number) => {
      const newArray: RemitInputs[] | undefined = [...rows];
      if (index === 0) {
        newArray.shift();
      } else {
        newArray.splice(index, 1);
      }

      setRows(newArray);
    },
    [rows]
  );

  const removeMask = (value: string) =>
    Number(value.replace('R$', '').replaceAll('.', '').replaceAll(',', '.'));

  const addRow = useCallback(
    (index: number) => {
      const newMinLimit = rows[index].maxLimit + 0.01;

      const newItem: RemitInputs = {
        approvers: [],
        maxLimit: 0,
        minApprovers: '',
        minLimit: Number(newMinLimit.toFixed(2)),
      };
      const array = [...rows, newItem];
      setRows(array);
    },
    [rows]
  );

  const onChange = useCallback(
    (index: number, type: 'min' | 'max' | 'minApprovers', text: string) => {
      const values = [...rows];

      if (type === 'min') {
        values[index].minLimit = removeMask(text);
        setRows(values);
      } else if (type === 'max') {
        values[index].maxLimit = removeMask(text);
        setRows(values);
      } else if (type === 'minApprovers') {
        values[index].minApprovers = text;
        setRows(values);
      }
    },
    [rows]
  );

  const options = useMemo(() => {
    return myGroups.map((item) => {
      return { label: item.name, value: item.id };
    });
  }, [myGroups]);

  const handleChange = (option: string[], index: number) => {
    const values = [...rows];
    values[index].approvers = option.map((item) => JSON.parse(item));
    setRows(values);
  };

  return (
    <Modal
      centered
      onCancel={handleClose}
      open={visible}
      footer={false}
      width={1300}
    >
      <h2 className="remit-modal__fixed-title">
        {translate('PAGES.PROFILE_MANAGER.REMIT.HEADER_TITLE')}
      </h2>
      <h3 className="remit-modal__title">{title}</h3>

      {rows?.map((item, key) => (
        <Form
          addRow={addRow}
          deleteRow={deleteRow}
          handleChange={handleChange}
          item={item}
          key={key}
          index={key}
          onChange={onChange}
          options={options}
          rows={rows}
        />
      ))}

      <Col>
        <Row className="remit-modal__end-of-screen">
          <span className="remit-modal__row__action-button">
            <AdvancedButton
              height="3.438rem"
              fullWidth={true}
              variant={'outlined'}
              type="button"
              onClick={handleClose}
              text={translate('PAGES.PROFILE_MANAGER.REMIT.CANCEL')}
            />
          </span>
          <span>
            <AdvancedButton
              disabled={modalInvalid}
              height="3.438rem"
              fullWidth={true}
              type="button"
              onClick={save}
              text={translate('PAGES.PROFILE_MANAGER.REMIT.SAVE')}
            />
          </span>
        </Row>
      </Col>
    </Modal>
  );
};

export default RemitModal;
